import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Calendar, Clock, User, CreditCard, CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const OPENING_DATE = new Date(2026, 8, 12); // Saturday 12 September 2026
OPENING_DATE.setHours(0, 0, 0, 0);

const BookingCalendar = ({ serviceLine }) => {
  const resolvedServiceLine =
    serviceLine ||
    new URLSearchParams(window.location.search).get('service_line') ||
    'young_drivers';
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sessionType, setSessionType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (today < OPENING_DATE) {
      return new Date(2026, 8, 1);
    }

    return today;
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Customer details
  const [customerData, setCustomerData] = useState({
    name: '',
    age: '',
    email: '',
    phone: ''
  });

  // Generate dates for current month view (12-month calendar)
  const generateMonthDates = (month) => {
    const dates = [];
    const year = month.getFullYear();
    const monthNum = month.getMonth();
    const daysInMonth = new Date(year, monthNum + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    const minBookingDate = today < OPENING_DATE ? OPENING_DATE : today;
    const maxBookingDate = new Date(today);
    maxBookingDate.setFullYear(today.getFullYear() + 1); // 12 months ahead

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthNum, day);
      date.setHours(0, 0, 0, 0); // Ensure consistent time
      // Only include future dates within 12-month window
      if (date >= minBookingDate && date <= maxBookingDate) {
        // Format as YYYY-MM-DD in local timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
      }
    }
    return dates;
  };

  const monthDates = generateMonthDates(currentMonth);

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() - 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minBookingDate = today < OPENING_DATE ? OPENING_DATE : today;
    const minMonth = new Date(
      minBookingDate.getFullYear(),
      minBookingDate.getMonth(),
      1
    );

    if (newMonth >= minMonth) {
      setCurrentMonth(newMonth);
    }
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + 1);
    const maxMonth = new Date();
    maxMonth.setFullYear(maxMonth.getFullYear() + 1);
    if (newMonth <= maxMonth) {
      setCurrentMonth(newMonth);
    }
  };

  // Fetch available slots when date is selected
  const fetchAvailableSlots = async (date) => {
    if (!sessionType || !date) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `${API_URL}/api/booking-diary/availability?date=${date}&session_type=${sessionType}`
      );
      const data = await response.json();
      
      if (response.ok) {
        setAvailableSlots(data.slots || []);
      } else {
        setError(data.detail || 'Failed to load availability');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate && sessionType) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, sessionType]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    setStep(3);
  };

  const handleTimeSelect = (slot) => {
    if (slot.status === 'available') {
      // For double sessions, we need to select this slot AND the next one
      if (sessionType === 'double') {
        // Find the index of the clicked slot
        const slotIndex = availableSlots.findIndex(s => s.start_time === slot.start_time);
        
        // Check if there's a next slot available
        if (slotIndex !== -1 && slotIndex < availableSlots.length - 1) {
          const nextSlot = availableSlots[slotIndex + 1];
          
          // Check if the next slot is also available (for 60-minute session)
          if (nextSlot.status === 'available') {
            // Both slots available - set the start time
            setSelectedTime(slot.start_time);
            setStep(4);
          } else {
            // Next slot not available
            setError('Double session requires two consecutive slots. The next slot is not available.');
            setTimeout(() => setError(''), 3000);
          }
        } else {
          // No next slot exists
          setError('Double session not available at this time. Please choose an earlier slot.');
          setTimeout(() => setError(''), 3000);
        }
      } else {
        // Single session - just select this slot
        setSelectedTime(slot.start_time);
        setStep(4);
      }
    }
  };

  const handleCustomerSubmit = () => {
    setStep(5);
  };

  const handleConfirmBooking = async () => {
    setLoading(true);
    setError('');

    try {
      // Create hold
      const holdResponse = await fetch(`${API_URL}/api/booking-diary/create-hold`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customerData.name,
          customer_age: parseInt(customerData.age),
          customer_email: customerData.email,
          customer_phone: customerData.phone,
          booking_date: selectedDate,
          start_time: selectedTime,
          session_type: sessionType,
          service_line: resolvedServiceLine
        })
      });

      const holdData = await holdResponse.json();

      if (!holdResponse.ok) {
        throw new Error(holdData.detail || 'Failed to create booking');
      }

      // Create payment session
      const paymentResponse = await fetch(`${API_URL}/api/kyle-drive/payments/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: holdData.booking_id,
          origin_url: window.location.origin
        })
      });

      const paymentData = await paymentResponse.json();

      if (!paymentResponse.ok) {
        throw new Error(paymentData.detail || 'Failed to create payment session');
      }

      // Redirect to Stripe
      window.location.href = paymentData.url;

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    const date = new Date(timeStr);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getPrice = () => {
    return sessionType === 'single' ? 39.99 : 79.98;
  };

  const getDuration = () => {
    return sessionType === 'single' ? '30 minutes' : '60 minutes';
  };

  return (
    <section id="book" className="relative py-20 bg-gradient-to-br from-gray-50 to-white overflow-hidden kyle-section-compact">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-[hsl(210,100%,12%)] mb-4">
            Book Your Drive
          </h2>
          <p className="text-lg text-gray-600">
            Choose your session, pick your time, and we'll see you on the track
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-2 sm:gap-4">
            {[1, 2, 3, 4, 5].map((num) => (
              <React.Fragment key={num}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= num
                      ? 'bg-gradient-to-br from-[hsl(210,100%,45%)] to-[hsl(180,80%,45%)] text-white'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {num}
                </div>
                {num < 5 && (
                  <div
                    className={`w-8 sm:w-16 h-1 ${
                      step > num ? 'bg-[hsl(210,100%,45%)]' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-200">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700">
              {error}
            </div>
          )}

          {/* Step 1: Choose Session Type */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-6 h-6 text-[hsl(210,100%,45%)] kyle-icon-pulse" />
                <h3 className="text-2xl font-bold text-[hsl(210,100%,12%)]">Choose Your Session</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <button
                  onClick={() => {
                    setSessionType('single');
                    setStep(2);
                  }}
                  className="p-6 border-2 border-gray-200 rounded-2xl hover:border-[hsl(210,100%,45%)] hover:shadow-lg transition-all text-left group"
                >
                  <div className="text-3xl font-bold text-[hsl(210,100%,45%)] mb-2">£39.99</div>
                  <div className="text-xl font-bold text-[hsl(210,100%,12%)] mb-2">Single Session</div>
                  <div className="text-gray-600 mb-4">30 minutes of driving</div>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>&bull; Perfect for first-timers</li>
                    <li>&bull; Learn basic controls</li>
                    <li>&bull; Build confidence</li>
                  </ul>
                </button>

                <button
                  onClick={() => {
                    setSessionType('double');
                    setStep(2);
                  }}
                  className="p-6 border-2 border-gray-200 rounded-2xl hover:border-[hsl(210,100%,45%)] hover:shadow-lg transition-all text-left group"
                >
                  <div className="text-3xl font-bold text-[hsl(210,100%,45%)] mb-2">£79.98</div>
                  <div className="text-xl font-bold text-[hsl(210,100%,12%)] mb-2">Double Session</div>
                  <div className="text-gray-600 mb-4">60 minutes of driving</div>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>&bull; Extended practice time</li>
                    <li>&bull; More complex manoeuvres</li>
                    <li>&bull; Better value</li>
                  </ul>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Choose Date - 12 Month Calendar */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-[hsl(210,100%,45%)] kyle-icon-pulse" />
                  <h3 className="text-2xl font-bold text-[hsl(210,100%,12%)]">Choose Your Date</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
              </div>

              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                <Button
                  variant="outline"
                  onClick={goToPreviousMonth}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Previous
                </Button>
                <div className="text-xl font-bold text-[hsl(210,100%,12%)]">
                  {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </div>
                <Button
                  variant="outline"
                  onClick={goToNextMonth}
                  className="flex items-center gap-2"
                >
                  Next <ArrowRight className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-7 gap-2 text-center mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-sm font-semibold text-gray-500">{day}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {/* Add empty cells for proper alignment */}
                {(() => {
                  // If we have dates in the month, use the first date's day of week for alignment
                  if (monthDates.length > 0) {
                    const [year, month, day] = monthDates[0].split('-').map(Number);
                    const firstDate = new Date(year, month - 1, day);
                    const firstDayOfWeek = firstDate.getDay();
                    return Array.from({ length: firstDayOfWeek }).map((_, i) => (
                      <div key={`empty-${i}`} className="p-4"></div>
                    ));
                  }
                  return null;
                })()}
                
                {monthDates.map((date) => {
                  // Parse the date string correctly in local timezone
                  const [year, month, day] = date.split('-').map(Number);
                  const dateObj = new Date(year, month - 1, day);
                  const dayNum = dateObj.getDate();
                  const dayOfWeek = dateObj.getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek];

                  return (
                    <button
                      key={date}
                      onClick={() => handleDateSelect(date)}
                      className={`p-4 border-2 rounded-xl hover:border-[hsl(210,100%,45%)] hover:shadow-md transition-all ${
                        isWeekend 
                          ? 'bg-orange-50 border-orange-200' 
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="text-xs text-gray-500 mb-1">{dayName}</div>
                      <div className="text-2xl font-bold text-[hsl(210,100%,12%)]">{dayNum}</div>
                      {isWeekend && <div className="text-xs text-orange-600 mt-1">Weekend</div>}
                    </button>
                  );
                })}
              </div>

              <p className="text-sm text-center text-gray-500 mt-4">
                Bookings available up to 12 months in advance
              </p>
            </div>
          )}

          {/* Step 3: Choose Time */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-[hsl(210,100%,45%)] kyle-icon-pulse" />
                  <h3 className="text-2xl font-bold text-[hsl(210,100%,12%)]">Choose Your Time</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(2)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
              </div>

              <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                <div className="font-semibold text-[hsl(210,100%,12%)]">{formatDate(selectedDate)}</div>
                <div className="text-sm text-gray-600">{sessionType === 'single' ? 'Single' : 'Double'} Session (£{getPrice()})</div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-12 h-12 border-4 border-[hsl(210,100%,45%)] border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading available times...</p>
                </div>
              ) : (
                <>
                  {sessionType === 'double' && (
                    <div className="mb-4 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-xl text-sm text-yellow-800">
                      <strong>Double Session:</strong> Selecting a time will book TWO consecutive 45-minute slots (60-minute driving session).
                    </div>
                  )}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-96 overflow-y-auto">
                    {availableSlots.map((slot, index) => {
                      // For double sessions, check if this slot + next slot are available
                      const isDoubleAvailable = sessionType === 'double' && 
                                                slot.status === 'available' && 
                                                index < availableSlots.length - 1 && 
                                                availableSlots[index + 1].status === 'available';
                      
                      const isSingleAvailable = sessionType === 'single' && slot.status === 'available';
                      
                      return (
                        <button
                          key={slot.display_time}
                          onClick={() => handleTimeSelect(slot)}
                          disabled={sessionType === 'double' ? !isDoubleAvailable : slot.status !== 'available'}
                          className={`p-3 rounded-xl font-semibold ${
                            (isSingleAvailable || isDoubleAvailable)
                              ? 'bg-green-50 border-2 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-400'
                              : 'bg-gray-100 border-2 border-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {slot.display_time}
                          {isDoubleAvailable && <div className="text-xs mt-1">+ next slot</div>}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 4: Enter Details */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <User className="w-6 h-6 text-[hsl(210,100%,45%)] kyle-icon-pulse" />
                  <h3 className="text-2xl font-bold text-[hsl(210,100%,12%)]">Your Details</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(3)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold text-[hsl(210,100%,12%)] mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={customerData.name}
                    onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[hsl(210,100%,45%)] focus:outline-none"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block font-semibold text-[hsl(210,100%,12%)] mb-2">Age *</label>
                  <input
                    type="number"
                    min={resolvedServiceLine === 'young_drivers' ? "12" : undefined}
                    value={customerData.age}
                    onChange={(e) => setCustomerData({ ...customerData, age: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[hsl(210,100%,45%)] focus:outline-none"
                    placeholder={resolvedServiceLine === 'young_drivers' ? "Must be 12 or older" : "Enter your age"}
                    required
                  />
                </div>

                <div>
                  <label className="block font-semibold text-[hsl(210,100%,12%)] mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={customerData.email}
                    onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[hsl(210,100%,45%)] focus:outline-none"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block font-semibold text-[hsl(210,100%,12%)] mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    value={customerData.phone}
                    onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[hsl(210,100%,45%)] focus:outline-none"
                    placeholder="07700 900123"
                    required
                  />
                </div>

                <Button
                  onClick={handleCustomerSubmit}
                  disabled={!customerData.name || !customerData.age || !customerData.email || !customerData.phone}
                  className="w-full bg-gradient-to-r from-[hsl(210,100%,45%)] to-[hsl(180,80%,45%)] hover:from-[hsl(210,100%,40%)] hover:to-[hsl(180,80%,40%)] text-white font-bold py-4"
                >
                  Continue to Review
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Review & Confirm */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[hsl(210,100%,45%)] kyle-icon-pulse" />
                  <h3 className="text-2xl font-bold text-[hsl(210,100%,12%)]">Review Your Booking</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(4)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Session Type</div>
                      <div className="font-bold text-[hsl(210,100%,12%)]">
                        {sessionType === 'single' ? 'Single Session' : 'Double Session'}
                      </div>
                      <div className="text-sm text-gray-600">{getDuration()}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Date & Time</div>
                      <div className="font-bold text-[hsl(210,100%,12%)]">{formatDate(selectedDate)}</div>
                      <div className="text-sm text-gray-600">{formatTime(selectedTime)}</div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
                  <div className="text-sm text-gray-600 mb-2">Customer Details</div>
                  <div className="space-y-1">
                    <div><span className="font-semibold">Name:</span> {customerData.name}</div>
                    <div><span className="font-semibold">Age:</span> {customerData.age}</div>
                    <div><span className="font-semibold">Email:</span> {customerData.email}</div>
                    <div><span className="font-semibold">Phone:</span> {customerData.phone}</div>
                  </div>
                </div>

                <div className="p-6 bg-gradient-to-br from-[hsl(210,100%,45%)] to-[hsl(180,80%,45%)] rounded-xl text-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm opacity-90">Total to Pay</div>
                      <div className="text-4xl font-bold">£{getPrice()}</div>
                    </div>
                    <CreditCard className="w-12 h-12 opacity-50 kyle-icon-pulse" />
                  </div>
                </div>

                <Button
                  onClick={handleConfirmBooking}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-6 text-lg"
                >
                  {loading ? 'Processing...' : 'Proceed to Payment'}
                </Button>

                <p className="text-xs text-center text-gray-500">
                  You'll be redirected to Stripe for secure payment. Your booking will be held for 10 minutes.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BookingCalendar;
